/**
 * KcsOrSuperAdminOnly — client-side route guard used to scope the
 * KCS-specific User Manual pages.
 *
 * Allowed:
 *  - super_admin (regardless of the client they're currently viewing as)
 *  - any user whose first client assignment resolves to slug === "kcs"
 *
 * Everyone else gets redirected to /forbidden.
 *
 * NB: the manual is currently authored for KCS operations. When another
 *     client wants the same page pattern we'll templatize per-client
 *     instead of widening this gate.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { supabase } from "@/lib/supabaseClient";

const ALLOWED_SLUG = "kcs";

export default function KcsOrSuperAdminOnly({ children }) {
  const { hasRole, assignments } = useAuth();
  const { activeClient } = useViewAsClient();
  const [state, setState] = useState({ checked: false, allowed: false });

  useEffect(() => {
    let cancelled = false;

    // Super admin bypass — always allowed.
    if (hasRole(["super_admin"])) {
      setState({ checked: true, allowed: true });
      return;
    }

    (async () => {
      // If a super_admin is view-as-ing a client, that client's slug wins.
      if (activeClient?.slug) {
        if (!cancelled) setState({ checked: true, allowed: activeClient.slug === ALLOWED_SLUG });
        return;
      }
      const myClientId = assignments?.find((a) => a.client_id)?.client_id;
      if (!myClientId) {
        if (!cancelled) setState({ checked: true, allowed: false });
        return;
      }
      const { data } = await supabase
        .from("lbs_clients")
        .select("slug")
        .eq("id", myClientId)
        .maybeSingle();
      if (!cancelled) setState({ checked: true, allowed: data?.slug === ALLOWED_SLUG });
    })();

    return () => { cancelled = true; };
  }, [hasRole, activeClient, assignments]);

  if (!state.checked) {
    return (
      <div className="p-8 text-sm text-slate-500" data-testid="manual-gate-loading">
        Checking access…
      </div>
    );
  }
  if (!state.allowed) return <Navigate to="/forbidden" replace />;
  return children;
}
